import { useLocale, useT } from '../i18n';
import { useApiData } from '../api/useApiData';
import { contentApi, type EvidenceDocument } from '../api/content';
import AsyncSection from '../components/shared/AsyncSection';

/* Security / Sovereignty / Team all read from the same evidence_documents
 * table (spec §23 note: keeping public evidence and client-portal
 * documents in separate tables is deliberate — see backend/prisma/schema.prisma).
 * Publishing an evidence_documents row without evidence_source is
 * blocked at the API layer (routes/evidence.ts), not just here — so an
 * empty result here honestly means "not published yet," consistent with
 * the spec's own [CONTENT REQUIRED] markers for Team bios and the
 * not-yet-certified state of Security. */
export default function TrustPage({ category, title }: { category: EvidenceDocument['category']; title: string }) {
  const locale = useLocale();
  const t = useT();
  const state = useApiData(() => contentApi.evidence(locale, category), [locale, category]);

  return (
    <section className="section" style={{ paddingTop: 160 }}>
      <div className="container">
        <div className="section-head reveal is-visible">
          <h1>{title}</h1>
        </div>
        <AsyncSection
          state={state}
          emptyMessage={
            category === 'team'
              ? "Team bios aren't published yet — pending internal confirmation. We'd rather say that plainly than invent names and titles."
              : "This page's content isn't published yet."
          }
          render={(docs) => (
            <div className="card-grid">
              {docs.map((d) => (
                <div className="card reveal is-visible" key={d.id}>
                  <h3>{d.title}</h3>
                  <p className="body-sm">{typeof d.body === 'string' ? d.body : JSON.stringify(d.body)}</p>
                  <p className="body-sm" style={{ opacity: 0.6, marginTop: 12 }}>
                    Source: {d.evidenceSource}
                  </p>
                </div>
              ))}
            </div>
          )}
        />
      </div>
    </section>
  );
}
